/**
 * One report with its comments, plus the writes available from the detail view.
 */

import { gql, useMutation, useQuery } from "@apollo/client";

import type { FeedbackReport, FeedbackStatus } from "@/types";
import {
  REPORT_ROW_FIELDS,
  type AttachmentRefInput,
} from "@/hooks/useFeedbackReports";

export const GET_FEEDBACK_REPORT = gql`
  query GetFeedbackReport($id: String!) {
    feedbackReport(id: $id) {
      ...FeedbackReportRowFields
      context
      resolutionNote
      comments {
        id
        reportId
        body
        createdById
        version
        createdAt
        updatedAt
        createdBy {
          id
          firstName
          lastName
          accessLevel
        }
        attachments {
          id
          mediaType
          filename
          caption
          contentType
          sizeBytes
          width
          height
          durationSeconds
          url
          thumbUrl
          createdAt
        }
      }
    }
  }
  ${REPORT_ROW_FIELDS}
`;

const CREATE_FEEDBACK_COMMENT = gql`
  mutation CreateFeedbackComment($input: FeedbackCommentCreateInput!) {
    createFeedbackComment(input: $input) {
      success
      message
    }
  }
`;

const SET_FEEDBACK_STATUS = gql`
  mutation SetFeedbackReportStatus($input: FeedbackStatusInput!) {
    setFeedbackReportStatus(input: $input) {
      success
      message
    }
  }
`;

const DELETE_FEEDBACK_COMMENT = gql`
  mutation DeleteFeedbackComment($id: String!, $version: Int!) {
    deleteFeedbackComment(id: $id, version: $version) {
      success
      message
    }
  }
`;

interface ReportQueryResult {
  feedbackReport: FeedbackReport | null;
}

export function useFeedbackReport(id: string | null) {
  const { data, loading, error, refetch } = useQuery<ReportQueryResult>(
    GET_FEEDBACK_REPORT,
    {
      variables: { id },
      skip: !id,
      fetchPolicy: "cache-and-network",
    },
  );

  const [commentMutation] = useMutation(CREATE_FEEDBACK_COMMENT);
  const [statusMutation] = useMutation(SET_FEEDBACK_STATUS);
  const [deleteCommentMutation] = useMutation(DELETE_FEEDBACK_COMMENT);

  const addComment = async (body: string, attachments: AttachmentRefInput[]) => {
    if (!id) return null;
    const result = await commentMutation({
      variables: { input: { reportId: id, body, attachments } },
    });
    // Refetch rather than patch: a new comment changes commentCount on the report
    // and appends to a list, and getting both right by hand is not worth it for
    // one comment.
    if (result.data?.createFeedbackComment?.success) await refetch();
    return result.data?.createFeedbackComment;
  };

  const setStatus = async (
    status: FeedbackStatus,
    version: number,
    resolutionNote?: string | null,
  ) => {
    if (!id) return null;
    const result = await statusMutation({
      variables: { input: { id, version, status, resolutionNote } },
    });
    if (result.data?.setFeedbackReportStatus?.success) await refetch();
    return result.data?.setFeedbackReportStatus;
  };

  /** Soft-delete one comment. Author, or INTERNAL+ moderating.
   *
   * Report deletion is not here — it is a row action on the list, so it lives on
   * useFeedbackReports where the refetch belongs. */
  const deleteComment = async (commentId: string, version: number) => {
    const result = await deleteCommentMutation({
      variables: { id: commentId, version },
    });
    if (result.data?.deleteFeedbackComment?.success) await refetch();
    return result.data?.deleteFeedbackComment;
  };

  return {
    report: data?.feedbackReport ?? null,
    loading,
    error,
    refetch,
    addComment,
    setStatus,
    deleteComment,
  };
}
